import React from "react";

interface SlideContent {
  title?: string;
  subtitle?: string;
  content?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const Entrance1Slide = {
  id: "entrance1-slide",
  name: "Entrance Slide (Modern Gradient)",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
    },
    {
      id: "content",
      label: "Intro Note",
      type: "text",
    },
  ],
  render: (content: SlideContent) => {
    const {
      title = "",
      subtitle = "",
      content: introNote = "",
      bgColor,
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    return (
      <div
        className="w-full h-full flex flex-col items-center justify-center text-white relative overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600"
        style={{ background: bgColor }}
      >
        {/* Animated Decorative Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-32 h-32 bg-white/10 rounded-br-full animate-pulse" />
          <div className="absolute bottom-0 right-0 w-40 h-40 bg-white/10 rounded-tl-full animate-pulse delay-100" />
          <div className="absolute top-10 right-10 w-20 h-20 bg-white/5 rounded-full animate-pulse delay-200" />
          <div className="absolute bottom-20 left-20 w-24 h-24 bg-white/5 rounded-full animate-pulse delay-300" />
        </div>

        {/* Content Container */}
        <div className="relative z-10 max-w-4xl mx-auto p-8 text-center">
          {/* Main Title with Animation */}
          <div
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) =>
              handleEdit("title")(e.currentTarget.textContent || "")
            }
            className="text-7xl font-extrabold mb-6 tracking-tight animate-fade-in-up cursor-text"
          >
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-purple-200">
              {title || "Enter title..."}
            </span>
          </div>

          {/* Subtitle with Animation */}
          <div
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) =>
              handleEdit("subtitle")(e.currentTarget.textContent || "")
            }
            className="text-3xl font-light mb-8 text-purple-100 animate-fade-in-up animation-delay-200 cursor-text"
          >
            {subtitle || "Enter subtitle..."}
          </div>

          {/* Animated Divider */}
          <div className="w-32 h-1 mx-auto bg-gradient-to-r from-white to-transparent mb-8 animate-width" />

          {/* Intro Note with Animation */}
          <div
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) =>
              handleEdit("content")(e.currentTarget.textContent || "")
            }
            className="text-xl text-purple-50 leading-relaxed max-w-2xl mx-auto italic animate-fade-in-up animation-delay-400 cursor-text"
          >
            {introNote || "Enter intro note..."}
          </div>

          {/* Bottom Decorative Element */}
          <div className="mt-12 flex justify-center space-x-3">
            <div className="w-3 h-3 rounded-full bg-white/60 animate-bounce delay-0" />
            <div className="w-3 h-3 rounded-full bg-white/40 animate-bounce delay-100" />
            <div className="w-3 h-3 rounded-full bg-white/20 animate-bounce delay-200" />
          </div>
        </div>

        {/* Bottom Wave Effect */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            className="w-full h-24 fill-current text-white/5"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
              opacity=".25"
            />
            <path
              d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
              opacity=".5"
            />
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" />
          </svg>
        </div>
      </div>
    );
  },
};

// Add required styles to the global CSS
const style = document.createElement("style");
style.textContent = `
  @keyframes fade-in-up {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes width {
    from {
      width: 0;
    }
    to {
      width: 8rem;
    }
  }

  .animate-fade-in-up {
    animation: fade-in-up 0.6s ease-out forwards;
  }

  .animate-width {
    animation: width 0.8s ease-out forwards;
  }

  .animation-delay-200 {
    animation-delay: 200ms;
  }

  .animation-delay-400 {
    animation-delay: 400ms;
  }
`;
document.head.appendChild(style);

declare global {
  interface Window {
    updateLiveConfig?: (config: SlideContent) => void;
  }
}

export default Entrance1Slide;
